import { useState, useEffect } from 'react';
import AuthGuard from '@/components/feature/AuthGuard';
import OrganizerLayout from './components/OrganizerLayout';
import { supabase } from '@/lib/supabase';

interface KPI {
  totalTeams: number;
  totalParticipants: number;
  paidTeams: number;
  pendingPayments: number;
}

function HorizontalBarChart({ data, dataKey, labelKey, color, height }: {
  data: { [key: string]: string | number }[];
  dataKey: string; labelKey: string; color: string; height: number;
}) {
  if (!data || data.length === 0) {
    return <div className="flex items-center justify-center" style={{ height }}><p className="text-sm text-foreground-400">No data yet</p></div>;
  }
  const maxVal = Math.max(...data.map((d) => Number(d[dataKey])), 1);
  const barH = 22; const gap = 6;
  const totalH = data.length * (barH + gap) + 40;
  return (
    <svg width="100%" height={Math.max(height, totalH)} viewBox={`0 0 400 ${totalH}`} preserveAspectRatio="xMidYMid meet">
      {data.map((d, i) => {
        const val = Number(d[dataKey]);
        const barW = (val / maxVal) * 240;
        const y = i * (barH + gap) + 20;
        return (
          <g key={i}>
            <text x={90} y={y + barH / 2 + 4} textAnchor="end" className="text-[10px]" fill="oklch(var(--foreground-500))" fontFamily="var(--font-body)">{String(d[labelKey]).slice(0, 14)}</text>
            <rect x={96} y={y} width={Math.max(barW, 3)} height={barH} rx="3" fill={color} opacity="0.8" />
            <text x={96 + Math.max(barW, 3) + 6} y={y + barH / 2 + 4} className="text-[10px] font-semibold" fill="oklch(var(--foreground-700))" fontFamily="var(--font-body)">{val}</text>
          </g>
        );
      })}
    </svg>
  );
}

export default function OrganizerDashboard() {
  const [kpi, setKPI] = useState<KPI>({ totalTeams: 0, totalParticipants: 0, paidTeams: 0, pendingPayments: 0 });
  const [psDistribution, setPsDistribution] = useState<{ name: string; count: number }[]>([]);
  const [collegeDistribution, setCollegeDistribution] = useState<{ name: string; count: number }[]>([]);
  const [deptDistribution, setDeptDistribution] = useState<{ name: string; count: number }[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');

  useEffect(() => {
    async function fetchAll() {
      setLoading(true);
      setFetchError('');
      try {
        const [{ data: teams }, { data: participants }, { data: payments }] = await Promise.all([
          supabase.from('teams').select('id, college_name, department, selected_problem_statement_id, status'),
          supabase.from('participants').select('id, department'),
          supabase.from('payments').select('id, status'),
        ]);

        const teamsArr = teams || [];
        const partsArr = participants || [];
        const paysArr = payments || [];

        setKPI({
          totalTeams: teamsArr.length,
          totalParticipants: partsArr.length,
          paidTeams: paysArr.filter((p) => p.status === 'paid').length,
          pendingPayments: paysArr.filter((p) => p.status === 'pending').length,
        });

        // College distribution
        const colCounts: Record<string, number> = {};
        teamsArr.forEach((t) => { colCounts[t.college_name] = (colCounts[t.college_name] || 0) + 1; });
        setCollegeDistribution(Object.entries(colCounts).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count));

        // Department distribution
        const deptCounts: Record<string, number> = {};
        partsArr.forEach((p) => { deptCounts[p.department] = (deptCounts[p.department] || 0) + 1; });
        setDeptDistribution(Object.entries(deptCounts).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count));

        // Problem statement distribution
        const psCounts: Record<string, number> = {};
        const psIds = teamsArr.filter((t) => t.selected_problem_statement_id).map((t) => t.selected_problem_statement_id);
        if (psIds.length > 0) {
          const { data: psData } = await supabase.from('problem_statements').select('id, title').in('id', psIds);
          const psMap: Record<string, string> = {};
          (psData || []).forEach((ps) => { psMap[ps.id] = ps.title; });
          teamsArr.forEach((t) => {
            if (t.selected_problem_statement_id && psMap[t.selected_problem_statement_id]) {
              const title = psMap[t.selected_problem_statement_id];
              psCounts[title] = (psCounts[title] || 0) + 1;
            }
          });
        }
        setPsDistribution(Object.entries(psCounts).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count));
      } catch (err) {
        setFetchError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    }
    fetchAll();
  }, []);

  const kpiCards = [
    { icon: 'ri-team-line', label: 'Total Teams', value: kpi.totalTeams, color: 'bg-primary-100 text-primary-600' },
    { icon: 'ri-user-line', label: 'Total Participants', value: kpi.totalParticipants, color: 'bg-accent-100 text-accent-600' },
    { icon: 'ri-check-double-line', label: 'Paid Teams', value: kpi.paidTeams, color: 'bg-accent-50 text-accent-600' },
    { icon: 'ri-time-line', label: 'Pending Payments', value: kpi.pendingPayments, color: 'bg-amber-100 text-amber-600' },
  ];

  return (
    <AuthGuard requiredRole="organizer">
      <OrganizerLayout title="Dashboard" breadcrumbs={[{ label: 'Dashboard' }]}>
        {fetchError && (
          <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm">
            Failed to load dashboard: {fetchError}. <button onClick={() => window.location.reload()} className="underline font-medium cursor-pointer">Retry</button>
          </div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
          {kpiCards.map((card) => (
            <div key={card.label} className="p-4 md:p-5 rounded-xl border border-background-200/70 bg-background-50">
              <div className={`w-9 h-9 rounded-lg ${card.color} flex items-center justify-center mb-3`}>
                <i className={`${card.icon} text-base`}></i>
              </div>
              <p className="text-xs text-foreground-500 whitespace-nowrap">{card.label}</p>
              <p className="text-xl md:text-2xl font-bold text-foreground-900 mt-0.5">
                {loading ? <span className="inline-block w-10 h-5 rounded bg-background-200 animate-pulse"></span> : card.value}
              </p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
          <div className="rounded-2xl border border-background-200/70 bg-background-50 p-5 md:p-6">
            <h3 className="font-heading text-sm font-bold text-foreground-950 mb-4">College-wise</h3>
            {loading ? <div className="h-52 flex items-center justify-center"><div className="w-6 h-6 border-2 border-primary-400 border-t-transparent rounded-full animate-spin"></div></div>
              : <HorizontalBarChart data={collegeDistribution} dataKey="count" labelKey="name" color="oklch(var(--primary-500))" height={200} />}
          </div>
          <div className="rounded-2xl border border-background-200/70 bg-background-50 p-5 md:p-6">
            <h3 className="font-heading text-sm font-bold text-foreground-950 mb-4">Department-wise</h3>
            {loading ? <div className="h-52 flex items-center justify-center"><div className="w-6 h-6 border-2 border-accent-400 border-t-transparent rounded-full animate-spin"></div></div>
              : <HorizontalBarChart data={deptDistribution} dataKey="count" labelKey="name" color="oklch(var(--accent-500))" height={200} />}
          </div>
          <div className="rounded-2xl border border-background-200/70 bg-background-50 p-5 md:p-6">
            <h3 className="font-heading text-sm font-bold text-foreground-950 mb-4">Problem Statement-wise</h3>
            {loading ? <div className="h-52 flex items-center justify-center"><div className="w-6 h-6 border-2 border-secondary-400 border-t-transparent rounded-full animate-spin"></div></div>
              : <HorizontalBarChart data={psDistribution} dataKey="count" labelKey="name" color="oklch(var(--secondary-500))" height={200} />}
          </div>
        </div>
      </OrganizerLayout>
    </AuthGuard>
  );
}